import { Component, OnInit, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { TableColumn } from 'src/app/shared/components/data-table/data-table.component';
import { EvaluationData, BidInfo, Document, BidEvaluationInfo, MeetingAttachment } from 'src/app/shared/models/app.models';
import { ApiService } from 'src/app/shared/services/api.service';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { TenderHelperService } from 'src/app/shared/services/tender-helper.service';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { FileUploadComponent } from 'src/app/shared/components/file-upload/file-upload.component';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'app-evaluation-scoring',
  templateUrl: './evaluation-scoring.component.html',
  styleUrls: ['./evaluation-scoring.component.scss']
})
export class EvaluationScoringComponent implements OnInit {
  @ViewChild('fileInput') fileInput!: ElementRef;
  @ViewChild('fileUploadComponent') fileUploadComponent!: FileUploadComponent;

  AppConstants = AppConstants;
  scoringForm: FormGroup;
  evaluationDetails: EvaluationData = {
    tenderId: '',
    evaluationType: 'Technical',
    evaluationId: '',
    prNumber: ''
  };
  uploadedFile: File | null = null;
  isDragOver = false;

  // Table Data
  allBids: BidEvaluationInfo[] = [];
  tableColumns: TableColumn[] = [];
  totalBids: number = 0;

  // Documents Dialog
  showDocumentsDialog: boolean = false;
  documents: Document[] = [];
  currentBidForDocuments: BidEvaluationInfo | null = null;
  
  
  currentAttachments: MeetingAttachment[] = [];
  isLoadingAttachments: boolean = false;
  
  // Confirmation Dialog
  showConfirmDialog: boolean = false;
  pendingBid: BidEvaluationInfo | null = null;
  pendingAction: 'approved' | 'rejected' | null = null;

  // View-only mode when evaluation is already submitted
  isReadOnly: boolean = false;
  evaluatorId: number = 0;

  constructor(
    private fb: FormBuilder,
    private _route: ActivatedRoute,
    private _router: Router,
    private apiService: ApiService,
    private toasterService: ToasterService,    
    private _loaderService: LoaderService,
    private tenderHelper: TenderHelperService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef
  ) {
    this.scoringForm = this.fb.group({
      evaluationReport: ['', Validators.required],
      recommendation: [''],
      generalRemarks: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    // Initialize table columns
    this.initializeTableColumns();

    this._route.queryParams.subscribe(async params => {
      this.evaluationDetails = {
        tenderId: params['tenderId'] || '',
        evaluationType: params['evaluationType'] || 'Technical',
        evaluationId: params['evaluationId'] || '',
        prNumber: params['prNumber'] || params['pr'] || ''
      };

      // Fetch evaluation scoring data if evaluationId is available
      if (this.evaluationDetails.evaluationId) {
        await this.fetchEvaluationScoringData();
      } else {
        // No tenderId provided, clear bids
        this.allBids = [];
        this.totalBids = 0;
        this.cdr.detectChanges();
      }
    });
  }

  async fetchEvaluationScoringData(): Promise<void> {
    if (!this.evaluationDetails.evaluationId) {
      console.warn('[Evaluation Scoring] No evaluation ID available to fetch scoring data');
      return;
    }

    try {
      this._loaderService.showLoader();

      // Call the API to get evaluation scoring data
      this.apiService.setBaseUrl(environment.API_URL);
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.EVALUATIONS}/${AppConstants.ACTIONS.GetEvaluationScoringData}/${encodeURIComponent(this.evaluationDetails.evaluationId)}`;

      const response: any = await lastValueFrom(
        this.apiService.getByUrl(url)
      );

      // Check if request was successful
      if (!response?.isSuccess) {
        const errorMsg = response?.errorMessageEn || response?.errorMessageAr || 'Failed to fetch evaluation scoring data';
        console.error(`[Evaluation Scoring] API Error fetching scoring data: ${errorMsg}`);
        return;
      }

      const data = response?.data;
      if (!data) {
        console.warn('[Evaluation Scoring] No data returned from API');
        return;
      }

      // Update evaluation details from response
      this.evaluationDetails.tenderId = data.tenderId || this.evaluationDetails.tenderId;
      this.evaluationDetails.evaluationType = data.evaluationType || this.evaluationDetails.evaluationType;
       this.evaluationDetails.prNumber = data.prNumber || this.evaluationDetails.prNumber;
       this.evaluationDetails.status = data.status || '';
       this.evaluationDetails.reviewComments = data.reviewComments || '';
      this.isReadOnly = data.isReadOnly || false;
      this.evaluatorId = data.evaluatorId || 0;     
      
      this.scoringForm.patchValue({
        recommendation: data.recommendation || '',
        generalRemarks: data.generalRemarks || ''
      });

      // Map API response to BidEvaluationInfo format
      const bidsData = data.bids || [];
      this.allBids = bidsData.map((bid: any): BidEvaluationInfo => ({
        id: bid.id || 0,
        vendorId: bid.vendorId || bid.vendorID || '',
        bidId: bid.bidId || '',
        vendorName: bid.vendorName || '',
        submissionDate: bid.submissionDate ? this.formatDate(bid.submissionDate) : '',
        decision: bid.decision ? bid.decision.toLowerCase() as 'approved' | 'rejected' : null,
        comments: bid.comments || '',
        rejectionReason: bid.rejectionReason || '',
        canChangeDecision: bid.canChangeDecision !== false
      }));

      this.totalBids = this.allBids.length;

      // If read-only, disable the form
      if (this.isReadOnly) {
        this.scoringForm.disable();
      }

      // Trigger change detection first to ensure fileUploadComponent is available
      this.cdr.detectChanges();

      // Small delay to ensure ViewChild is resolved after change detection
      setTimeout(async () => {
        await this.loadEvaluationAttachments();
      }, 100);
    } catch (error: any) {
      const errorMessage = error?.message || 'Unknown error occurred';
      console.error(`[Evaluation Scoring] Exception while fetching scoring data:`, errorMessage, error);
    } finally {
      this._loaderService.hideLoader();
    }
  }

  private formatDate = (dateValue: any) => this.tenderHelper.formatDateForDisplay(dateValue);

  private initializeTableColumns(): void {
    // Initialize table columns
    this.tableColumns = [
      {
        field: 'bidId',
        header: 'Bid ID',
        type: 'text'
      },
      {
        field: 'vendorName',
        header: 'Vendor',
        type: 'text'
      },
      {
        field: 'status',
        header: 'Status',
        type: 'badge',
        badgeColor: (row: BidInfo) => {
          if (row.status === 'Under Evaluation') return 'blue';
          if (row.status === 'Evaluated') return 'green';
          if (row.status === 'Pending Evaluation') return 'orange';
          return 'gray';
        }
      },
      {
        field: 'actions',
        header: 'Action',
        type: 'action',
        actions: [
          {
            action: 'approve',
            title: 'Approve'
          },
          {
            action: 'reject',
            title: 'Reject'
          }
        ]
      }
    ];
  }

  onTableActionClick(event: {action: string, row: any}): void {
    if (event.action === 'approve') {
      this.onApproveBid(event.row);
    } else if (event.action === 'reject') {
      this.onRejectBid(event.row);
    }
  }

  onApproveBid(bid: BidInfo): void {
    console.log('Bid approved:', bid);
    // TODO: Save to backend
  }

  onRejectBid(bid: BidInfo): void {
    console.log('Bid rejected:', bid);
    // TODO: Save to backend
  }

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFileSelection(files[0]);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFileSelection(input.files[0]);
    }
  }

  private handleFileSelection(file: File): void {
    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const allowedExtensions = ['.pdf', '.doc', '.docx'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      alert('Please upload a PDF, DOC, or DOCX file.');
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      alert('File size must be less than 10MB.');
      return;
    }

    this.uploadedFile = file;
    this.scoringForm.patchValue({ evaluationReport: file.name });
    this.scoringForm.get('evaluationReport')?.markAsTouched();
  }

  removeFile(): void {
    this.uploadedFile = null;
    this.scoringForm.patchValue({ evaluationReport: '' });
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  private prepareFormData(saveAsDraft: boolean) {
    const decisions = this.allBids
      .filter(bid => !!bid.decision)
      .map(bid => ({
        bidId: bid.id,
        decision: this.toEvaluationDecisionPayload(bid.decision),
        comments: bid.comments || '',
        rejectReason: bid.rejectionReason || ''
      }));

    return {
      ...this.evaluationDetails,
      evaluatorId: this.evaluatorId,
      recommendation: this.scoringForm.get('recommendation')?.value || '',
       generalRemarks: this.scoringForm.get('generalRemarks')?.value || '',
       saveAsDraft,
       decisions,
    };
  }

  private toEvaluationDecisionPayload(decision: BidEvaluationInfo['decision']): 'Approved' | 'Rejected' | 'Pending' {
    if (decision === 'approved') {
      return 'Approved';
    }
    if (decision === 'rejected') {
      return 'Rejected';
    }
    return 'Pending';
  }

  private async handleFormSubmission(action: 'draft' | 'submit'): Promise<void> {
    
    if (action === 'submit' && !this.scoringForm.valid) {
      this.scoringForm.markAllAsTouched();
      return;
    }

    const formData = this.prepareFormData(action === 'draft');

    this._loaderService.showLoader();

    try {
      this.apiService.setBaseUrl(environment.API_URL);

      // Get actual File objects from the file-upload component
      const uploadedFiles: File[] = this.fileUploadComponent?.getUploadedFiles() || [];
      const hasFiles = uploadedFiles.length > 0;

      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.EVALUATIONS}/${AppConstants.ACTIONS.SubmitDecisions}`;

      let response: any;

      // If files are present, use FormData (multipart/form-data) via ApiService
      if (hasFiles) {
        // Add processName to payload for file attachment type
        const payloadWithProcessName = {
          ...formData,
          processName: 'Evaluation'
        };

        // Use ApiService multipart methods which handle headers automatically
        response = await lastValueFrom(this.apiService.postMultipartByUrlByJSON(url, payloadWithProcessName, uploadedFiles));
      } else {
        // No files, use regular JSON request
        response = await lastValueFrom(this.apiService.postByUrl(url, formData));
      }

      // Handle response - check for success
      // SubmitDecisions currently returns a raw boolean from the API, while other
      // endpoints use DefaultResponse. Support both contracts during the transition.
      const isSuccess = response === true || this.tenderHelper.isSuccessResponse(response);

      if (isSuccess) {
        const actionMessages = {
          'submit': 'Evaluation Submitted successfully',
          'draft': 'Evaluation draft saved successfully',
        };

        this.toasterService.showSuccess(actionMessages[action], 'Success');
        this.evaluationDetails.status = action === 'draft' ? 'Draft' : 'Evaluated';
      } else {
        const errorMessage = this.tenderHelper.getResponseErrorMessage(response, 'Failed to submit evaluation');
        this.toasterService.showError(errorMessage, 'Error');
        return;
      }

      if (action === 'submit') {
        setTimeout(() => {
          this._router.navigate([`/${AppConstants.ROUTES.EVALUATIONS}`]);
        }, 1500);
      }

    } catch (error: any) {
      const responseBody = error?.error;
      const errorMessage = (typeof responseBody === 'string' ? responseBody : responseBody?.error)
        || responseBody?.errorMessageEn
        || responseBody?.message
        || this.tenderHelper.getErrorMessage(error)
        || 'Failed to submit evaluation';
      console.error(`[Evaluation Scoring] Error:`, errorMessage, error);
      this.toasterService.showError(errorMessage, 'Error');
    } finally {
      this._loaderService.hideLoader();
    }
  }

  onCancel(): void {
    this._router.navigate([`/${AppConstants.ROUTES.EVALUATIONS}`]);
  }

  onSaveDraft(): void {
    this.handleFormSubmission('draft');
  }

  onSubmit(): void {
    this.handleFormSubmission('submit');
  }

  goBack(): void {
    this._router.navigate([`/${AppConstants.ROUTES.EVALUATIONS}`]);
  }

  onApprove(bid: BidEvaluationInfo): void {
    if (this.isReadOnly || bid.canChangeDecision === false) return;
    this.pendingBid = bid;
    this.pendingAction = 'approved';
    this.showConfirmDialog = true;
  }

  onReject(bid: BidEvaluationInfo): void {
    if (this.isReadOnly || bid.canChangeDecision === false) return;
    this.pendingBid = bid;
    this.pendingAction = 'rejected';
    this.showConfirmDialog = true;
  }

  confirmAction(): void {
    if (this.pendingBid && this.pendingAction) {
      this.pendingBid.decision = this.pendingAction;
    }
    this.cancelAction();
  }

  cancelAction(): void {
    this.showConfirmDialog = false;
    this.pendingBid = null;
    this.pendingAction = null;
  }

  getConfirmDialogMessage(): string {
    if (!this.pendingBid || !this.pendingAction) return '';
    const action = this.pendingAction === 'approved' ? 'approved' : 'rejected';
    return `Are you sure you want to ${action} bid ${this.pendingBid.bidId}?`;
  }

  /**
   * Get the decision for a specific bid
   * @param bidId The bid ID
   * @returns 'approved', 'rejected', or null if no decision made
   */
  getBidDecision(bidId: string): 'approved' | 'rejected' | null {
      return this.allBids.find(b => b.bidId === bidId)?.decision ?? null;
  }
  
  onBidRemarksChange(bidId: string, remarks: string): void {
    const bid = this.allBids.find(b => b.bidId === bidId);
    if (bid) {
      bid.comments = remarks;
    }
  }
  
  onBidRejectionReasonChange(bidId: string, remarks: string): void {
    const bid = this.allBids.find(b => b.bidId === bidId);
    if (bid) {
      bid.rejectionReason = remarks;
    }
  }

  
  getBidRemarks(bidId: string): string {
    return this.allBids.find(b => b.bidId === bidId)?.comments ?? '';
  }

  async viewDocuments(bid: BidEvaluationInfo): Promise<void> {
    this.currentBidForDocuments = bid;
    this.showDocumentsDialog = true;
    await this.loadBidDocuments(bid);
  }

  closeDocumentsDialog(): void {
    this.showDocumentsDialog = false;
    this.currentBidForDocuments = null;
  }

  async loadBidDocuments(bid: BidEvaluationInfo): Promise<void> {
    this.documents = [];

    if (!bid?.bidId || !bid?.vendorId) {
      console.warn('[Evaluation Scoring] Cannot load bid documents without bidId and vendorId', bid);
      return;
    }

    try {
      this.isLoadingAttachments = true;
      const documentsUrl = `${environment.API_URL}/api/Documents/GetDocuments`;
      const documentsDto = {
        applicationId: bid.vendorId,
        processName: 'Bidding',
        processID: bid.bidId
      };
      const response: any = await lastValueFrom(this.apiService.postByUrl(documentsUrl, documentsDto));
      const docs = response?.isSuccess && Array.isArray(response?.data) ? response.data : [];
      const filteredDocs = docs.filter((doc: any) => this.shouldShowBidDocumentForEvaluation(doc));

      this.documents = filteredDocs.map((doc: any): Document => ({
        name: doc.name || doc.Name || 'Document',
        attachmentId: doc.attachmentId || doc.AttachmentId || '',
        attachmentFor: doc.attachmentFor || doc.AttachmentFor || '',
        size: this.formatFileSize(doc.fileSize || doc.FileSize || 0),
        uploadedDate: doc.createdAt || doc.CreatedAt || doc.createdDate || doc.CreatedDate || '',
        expiryDate: doc.documentExpiryDate || doc.DocumentExpiryDate || doc.expiryDate || doc.ExpiryDate || '',
        fileContent: doc.fileContent || doc.FileContent || '',
        mimeType: doc.mimeType || doc.MimeType || '',
        isUnsealed: this.isBidDocumentUnsealed(doc),
        filePath: doc.filePath || doc.FilePath || ''
      }));
    } catch (error) {
      console.error('[Evaluation Scoring] Error loading bid documents:', error);
      this.toasterService.showError('Failed to load bid documents', 'Error');
    } finally {
      this.isLoadingAttachments = false;
      this.cdr.detectChanges();
    }
  }

  private shouldShowBidDocumentForEvaluation(doc: any): boolean {
    const evaluationType = (this.evaluationDetails.evaluationType || '').toString().trim().toLowerCase().replace(/\s+/g, '');
    const attachmentFor = this.normalizeBidDocumentType(doc);

    if (evaluationType === 'technical') {
      return attachmentFor === 'technicalproposal' || attachmentFor === 'technical';
    }

    if (evaluationType === 'commercial') {
      return attachmentFor === 'commercialproposal' || attachmentFor === 'commercial';
    }

    if (evaluationType === 'combined') {
      return attachmentFor === 'technicalproposal' || attachmentFor === 'technical' ||
        attachmentFor === 'commercialproposal' || attachmentFor === 'commercial';
    }

    if (evaluationType === 'negotiation') {
      return attachmentFor === 'technicalproposal' ||
        attachmentFor === 'technical' ||
        attachmentFor === 'commercialproposal' ||
        attachmentFor === 'commercial';
    }

    return attachmentFor === 'technicalproposal' ||
      attachmentFor === 'technical' ||
      attachmentFor === 'commercialproposal' ||
      attachmentFor === 'commercial';
  }

  isEvaluationBidDocumentSealed(document: Document): boolean {
    if (this.isPracticeBid(this.currentBidForDocuments?.bidId)) {
      return false;
    }

    const attachmentFor = this.normalizeBidDocumentType(document);
    const isProposalDocument = attachmentFor === 'technicalproposal' ||
      attachmentFor === 'technical' ||
      attachmentFor === 'commercialproposal' ||
      attachmentFor === 'commercial';

    return isProposalDocument && document.isUnsealed === false;
  }

  private isPracticeBid(bidId?: string): boolean {
    const normalizedBidId = (bidId || '').toString().trim().toUpperCase();
    return normalizedBidId.startsWith('PRA-') && !normalizedBidId.startsWith('FWA-PRA-');
  }

  private isBidDocumentUnsealed(doc: any): boolean {
    const rawValue = doc?.isUnsealed ?? doc?.IsUnsealed ?? doc?.unsealed ?? doc?.Unsealed;
    if (typeof rawValue === 'boolean') {
      return rawValue;
    }

    const status = (rawValue ?? doc?.sealStatus ?? doc?.SealStatus ?? doc?.documentStatus ?? doc?.DocumentStatus ?? doc?.status ?? doc?.Status ?? '')
      .toString()
      .trim()
      .toLowerCase();

    return status === 'unsealed' || status === 'open' || status === 'opened';
  }
  private normalizeBidDocumentType(doc: any): string {
    return (doc?.attachmentFor || doc?.AttachmentFor || doc?.documentType || doc?.DocumentType || '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '');
  }
  formatFileSize(bytes: number | null | undefined): string {
    if (!bytes || bytes === 0) {
      return '0.000 MB';
    }
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(3)} MB`;
  }

  downloadDocument(document: Document): void {
    if (document.fileContent) {
      window.open(document.fileContent, '_blank');
      return;
    }

    if (document.attachmentId) {
      window.open(`${environment.API_URL}/api/Documents/DownloadDocument/${document.attachmentId}`, '_blank');
      return;
    }

    this.toasterService.showError('Document is not available for download', 'Error');
  }

  trackByDocument(index: number, document: Document): string {
    return document.name;
  }

  trackByBidId(index: number, bid: BidEvaluationInfo): string {
    return bid.bidId;
  }

  trackByAttachment(index: number, attachment: MeetingAttachment): string {
    return attachment.name || index.toString();
  }

  async loadEvaluationAttachments(): Promise<void> {
    if (!this.evaluationDetails) {
      console.warn('[Evaluation Scoring] No evaluation details available');
      return;
    }

    if (!this.fileUploadComponent) {
      console.warn('[Evaluation Scoring] File upload component not available yet');
      return;
    }

    try {
      // Fetch documents for this agenda item
      const documentsUrl = `${environment.API_URL}/api/Documents/GetDocuments`;
      const documentsDto = {
        applicationId: this.evaluationDetails?.evaluationId?.toString(),
        processName: 'Evaluation',
        processID: this.evaluationDetails?.evaluationId?.toString()
      };

      const documentsResponse: any = await lastValueFrom(this.apiService.postByUrl(documentsUrl, documentsDto));

      // Handle both array response and string response ("No documents found")
      if (documentsResponse?.isSuccess && documentsResponse?.data) {
        // Check if data is an array (documents) or string (no documents message)
        if (Array.isArray(documentsResponse.data) && documentsResponse.data.length > 0) {
          const documents = documentsResponse.data;

          // Fetch all files first, then add them to the component
          // Option 1: Direct blob access (requires CORS configured on Azure Blob Storage) - RECOMMENDED
          // Option 2: Backend proxy (fallback if CORS not configured)
          const USE_DIRECT_BLOB_ACCESS = true; // Set to false to use backend proxy
          
          const filePromises = documents
            .filter((doc: any) => {
              if (USE_DIRECT_BLOB_ACCESS) {
                return doc.fileContent && doc.name; // Need fileContent (SAS URL) for direct access
              } else {
                return doc.attachmentId && doc.name; // Need attachmentId for backend proxy
              }
            })
            .map(async (doc: any) => {
              try {
                let blob: Blob;
                let contentType: string;
                
                if (USE_DIRECT_BLOB_ACCESS) {
                  // DIRECT ACCESS: Fetch directly from Azure Blob Storage (requires CORS)
                  // This is the BEST PRACTICE approach - faster, more scalable
                  const fileUrl = doc.fileContent; // This is the SAS URL from backend
                  
                  const response = await fetch(fileUrl, {
                    method: 'GET',
                    headers: {
                      'Origin': window.location.origin // Required for CORS
                    }
                  });
                  
                  if (!response.ok) {
                    console.error(`Failed to fetch file from URL: ${fileUrl}`, response.statusText);
                    return null;
                  }
                  
                  blob = await response.blob();
                  contentType = doc.mimeType || blob.type || 'application/octet-stream';
                } else {
                  // BACKEND PROXY: Use backend endpoint (fallback if CORS not configured)
                  const downloadUrl = `${environment.API_URL}/api/Documents/DownloadDocument/${doc.attachmentId}`;
                  
                  const response = await this.http.get(downloadUrl, { 
                    responseType: 'blob',
                    observe: 'response'
                  }).toPromise();
                  
                  if (!response || !response.body) {
                    console.error(`Failed to fetch file for attachment ${doc.attachmentId}`);
                    return null;
                  }
                  
                  blob = response.body;
                  contentType = response.headers.get('content-type') || doc.mimeType || 'application/octet-stream';
                }
                
                // Create a File object from the blob with proper name and type
                const file = new File([blob], doc.name, { 
                  type: contentType,
                  lastModified: Date.now()
                });

                return {
                  fileName: doc.name,
                  file: file,
                  fileType: contentType
                };
              } catch (error: any) {
                // If direct access fails with CORS error, log helpful message
                if (USE_DIRECT_BLOB_ACCESS && error?.message?.includes('CORS')) {
                  console.error(`CORS error loading attachment ${doc.name}. Please configure CORS on Azure Blob Storage or set USE_DIRECT_BLOB_ACCESS to false.`, error);
                } else {
                  console.error(`Error loading attachment ${doc.name}:`, error);
                }
                return null;
              }
            });

          // Wait for all files to be fetched
          const fileResults = await Promise.all(filePromises);
          
          // Filter out any failed fetches and add files to the component
          const validFiles = fileResults.filter(result => result !== null);
          
          for (const fileResult of validFiles) {
            if (fileResult) {
              // Add file to the file-upload component
              // pushFileToUploadedFiles expects Blob, but File extends Blob so this works
              this.fileUploadComponent.pushFileToUploadedFiles(
                fileResult.fileName, 
                fileResult.file, 
                fileResult.fileType
              );
            }
          }
        }
        // If data is a string, it means "No documents found" - do nothing
      }
    } catch (error) {
      console.error('Error loading agenda item attachments:', error);
      // Don't show error to user - attachments are optional
    }
  } 
}

